<template>
    <portal to="main-dialog-portal">
    <div v-if="isShown" class="mp-dialog-backdrop" @click.self="handleBackdropClose">
        <div class="mp-dialog" :id="id" :style="dialogStyle" role="dialog" aria-modal="true">
            <div v-if="showHeader" class="mp-dialog-header">
                <slot name="modal-header" :close="close">
                    <div class="d-flex align-items-center">
                        <img v-if="icon" :src="icon" class="mp-dialog-icon mr-3" />
                        <div>
                            <h5 class="mp-dialog-title mb-0">{{ title }}</h5>
                            <small v-if="subtitle" class="mp-dialog-subtitle">{{ subtitle }}</small>
                        </div>
                    </div>
                    <button class="mp-dialog-close" @click="close">
                        <i class="bi bi-x"></i>
                    </button>
                </slot>
            </div>

            <div class="mp-dialog-body" :class="{ 'mp-dialog-body--flush': hideHeader }">
                <slot />
            </div>

            <div v-if="showFooter" class="mp-dialog-footer">
                <slot name="footer" :ok="ok" :cancel="cancel" :close="close">
                    <slot name="modal-footer" :ok="ok" :cancel="cancel" :close="close">
                        <b-button v-if="!hideCancel && !okOnly" :variant="cancelVariant" @click="cancel">
                            {{ cancelTitle }}
                        </b-button>
                        <b-button v-if="!hideOk" :variant="okVariant" :disabled="okDisabled" @click="ok">
                            {{ okTitle }}
                        </b-button>
                    </slot>
                </slot>
            </div>
        </div>
    </div>
    </portal>
</template>

<script>
export default {
    name: 'MainDialog',
    inheritAttrs: false,
    props: {
        id: {
            type: String,
            default: null,
        },
        show: {
            type: Boolean,
            default: null,
        },
        value: {
            type: Boolean,
            default: null,
        },
        visible: {
            type: Boolean,
            default: null,
        },
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        icon: {
            type: String,
            default: null,
        },
        maxWidth: {
            type: String,
            default: null,
        },
        size: {
            type: String,
            default: null,
        },
        hideHeader: {
            type: Boolean,
            default: false,
        },
        hideFooter: {
            type: Boolean,
            default: false,
        },
        hideCancel: {
            type: Boolean,
            default: false,
        },
        hideOk: {
            type: Boolean,
            default: false,
        },
        okOnly: {
            type: Boolean,
            default: false,
        },
        okTitle: {
            type: String,
            default: 'OK',
        },
        cancelTitle: {
            type: String,
            default: 'Cancel',
        },
        okVariant: {
            type: String,
            default: 'primary',
        },
        cancelVariant: {
            type: String,
            default: 'secondary',
        },
        okDisabled: {
            type: Boolean,
            default: false,
        },
        noCloseOnBackdrop: {
            type: Boolean,
            default: false,
        },
        noCloseOnEsc: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localShow: false,
        };
    },
    computed: {
        isShown() {
            if (this.show !== null) return this.show;
            if (this.value !== null) return this.value;
            if (this.visible !== null) return this.visible;
            return this.localShow;
        },
        showHeader() {
            return !this.hideHeader;
        },
        showFooter() {
            if (this.hideFooter) return false;
            if (this.$slots.footer || this.$scopedSlots.footer || this.$slots['modal-footer'] || this.$scopedSlots['modal-footer']) return true;

            return this.value !== null || this.visible !== null || !!this.id;
        },
        dialogStyle() {
            return { maxWidth: this.maxWidth || this.sizeMaxWidth };
        },
        sizeMaxWidth() {
            const sizes = {
                sm: '360px',
                md: '520px',
                lg: '800px',
                xl: '1140px',
            };

            return sizes[this.size] || '520px';
        },
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown);

            if (this.id && this.$root) {
                this.$root.$on('bv::show::modal', this.handleRootShow);
                this.$root.$on('bv::hide::modal', this.handleRootHide);
                this.$root.$on('main-dialog::show', this.handleRootShow);
                this.$root.$on('main-dialog::hide', this.handleRootHide);
            }
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown);

            if (this.id && this.$root) {
                this.$root.$off('bv::show::modal', this.handleRootShow);
                this.$root.$off('bv::hide::modal', this.handleRootHide);
                this.$root.$off('main-dialog::show', this.handleRootShow);
                this.$root.$off('main-dialog::hide', this.handleRootHide);
            }
    },
    methods: {
        createModalEvent(trigger) {
            return {
                trigger,
                defaultPrevented: false,
                preventDefault() {
                    this.defaultPrevented = true;
                },
            };
        },
        setShown(value, trigger = value ? 'show' : 'hide') {
            let event = null;
            if (!value) {
                event = this.createModalEvent(trigger);
                this.$emit('hide', event);
                if (event.defaultPrevented) return;
            }

            this.localShow = value;
            this.$emit('input', value);
            this.$emit('update:visible', value);

            if (!value) {
                this.$emit('close');
                if (this.id && this.$root) {
                    this.$root.$emit('main-dialog::hide', event, this.id);
                    this.$root.$emit('bv::modal::hide', event, this.id);
                }
            }
        },
        close() {
            this.setShown(false, 'close');
        },
        cancel() {
            const event = this.createModalEvent('cancel');
            this.$emit('cancel', event);
            if (!event.defaultPrevented) {
                this.setShown(false, 'cancel');
            }
        },
        ok() {
            const event = this.createModalEvent('ok');
            this.$emit('ok', event);
            if (!event.defaultPrevented) {
                this.setShown(false, 'ok');
            }
        },
        handleBackdropClose() {
            if (!this.noCloseOnBackdrop) {
                this.close();
            }
        },
        handleKeydown(event) {
            if (this.isShown && event.key === 'Escape' && !this.noCloseOnEsc) {
                this.close();
            }
        },
        handleRootShow(id) {
            if (id === this.id) {
                this.setShown(true, 'show');
            }
        },
        handleRootHide(id) {
            if (id === this.id) {
                this.setShown(false, 'hide');
            }
        },
    },
};
</script>

<style scoped>
.mp-dialog-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    z-index: 1060;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    backdrop-filter: blur(2px);
}

.mp-dialog {
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 12px;
    width: 100%;
    max-width: 520px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
    color: var(--text-primary);
}

.mp-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--border-default);
    flex-shrink: 0;
}

.mp-dialog-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    object-fit: contain;
    background: var(--bg-card-secondary);
    padding: 4px;
}

.mp-dialog-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary);
}

.mp-dialog-subtitle {
    color: var(--text-secondary);
    font-size: 0.82rem;
}

.mp-dialog-close {
    background: none;
    border: none;
    font-size: 1.4rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    color: var(--text-secondary);
    transition: color 0.15s;
}
.mp-dialog-close:hover { color: var(--text-primary); }

.mp-dialog-body {
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
}

.mp-dialog-body--flush {
    padding-top: 1.5rem;
}

.mp-dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--border-default);
    flex-shrink: 0;
}

.mp-dialog-footer ::v-deep .d-flex {
    gap: 0.5rem;
}

.mp-dialog-footer ::v-deep .btn {
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    min-height: 36px;
    transition: all 0.18s ease;
}

.mp-dialog-footer ::v-deep .btn-primary {
    background: var(--primary);
    border-color: var(--primary);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.mp-dialog-footer ::v-deep .btn-primary:hover {
    opacity: 0.88;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
}

.mp-dialog-footer ::v-deep .btn-warning {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.mp-dialog-footer ::v-deep .btn-warning:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
}

.mp-dialog-footer ::v-deep .btn-outline-secondary,
.mp-dialog-footer ::v-deep .btn-outline-primary {
    border-color: var(--border-hover);
    color: var(--text-secondary);
    background: transparent;
}

.mp-dialog-footer ::v-deep .btn-outline-secondary:hover,
.mp-dialog-footer ::v-deep .btn-outline-primary:hover {
    background: var(--bg-card-secondary);
    border-color: var(--border-default);
    color: var(--text-primary);
}
</style>
